import { getApiBaseUrl } from "../utils/getApiBaseUrl";

const API_BASE_URL = getApiBaseUrl();

const AUTH_TOKEN_KEY = "authToken";
const CURRENT_USER_KEY = "currentUser";

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function toReadableError(payload, fallbackMessage) {
  return (
    payload?.message ||
    payload?.error ||
    fallbackMessage
  );
}

function sanitizeUser(user) {
  if (!user || typeof user !== "object") return null;
  const next = { ...user };
  if ("password" in next) delete next.password;
  return next;
}

export function getAuthToken() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(AUTH_TOKEN_KEY) || "";
}

export function getCurrentUser() {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(CURRENT_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** GET /oauth/me — refreshes localStorage user when token is present. Returns null if no token. */
export async function fetchOauthMe() {
  const token = getAuthToken();
  if (!token) return null;
  const response = await fetch(`${API_BASE_URL}/oauth/me`, {
    method: "GET",
    headers: { Accept: "application/json", ...authHeaders() },
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    const err = new Error(toReadableError(payload, "Session expired."));
    err.status = response.status;
    throw err;
  }
  if (!payload?.user) {
    throw new Error("Session refresh failed.");
  }
  setAuthSession(token, payload.user);
  return payload;
}

export function setAuthSession(token, user) {
  if (typeof window === "undefined") return;
  if (!token) return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  if (user) {
    window.localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(sanitizeUser(user)));
  }
}

export function clearAuthSession() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(CURRENT_USER_KEY);
}

export function authHeaders() {
  const token = getAuthToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function login(email, password) {
  const response = await fetch(`${API_BASE_URL}/oauth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(toReadableError(payload, "Login failed. Please try again."));
  }
  if (!payload?.token || !payload?.user) {
    throw new Error("Login failed. Missing session data.");
  }
  setAuthSession(payload.token, payload.user);
  return payload;
}

export async function register(payload) {
  const response = await fetch(`${API_BASE_URL}/oauth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(toReadableError(data, "Registration failed. Please try again."));
  }
  if (!data?.token || !data?.user) {
    throw new Error("Registration completed but no session was returned.");
  }
  setAuthSession(data.token, data.user);
  return data;
}

export async function forgotPassword(email) {
  const response = await fetch(`${API_BASE_URL}/oauth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(
      toReadableError(payload, "Unable to send password reset code right now.")
    );
  }
  return payload;
}

export async function addEmailToAccount({ email, password, confirmPassword }) {
  const response = await fetch(`${API_BASE_URL}/oauth/account/add-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify({ email, password, confirmPassword }),
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(toReadableError(payload, "Unable to add email to this account."));
  }
  if (!payload?.user) {
    throw new Error("Invalid server response.");
  }
  const t = getAuthToken();
  setAuthSession(t, payload.user);
  return payload;
}

export async function changePassword({ currentPassword, newPassword, confirmPassword }) {
  const response = await fetch(`${API_BASE_URL}/oauth/account/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(toReadableError(payload, "Unable to change password."));
  }
  if (payload?.user) {
    const t = getAuthToken();
    setAuthSession(t, payload.user);
  }
  return payload;
}

export async function resetPassword(token, password) {
  let email = "";
  let code = "";
  if (token && typeof token === "object") {
    email = String(token.email || "").trim();
    code = String(token.code || "").trim();
  } else {
    code = String(token || "").trim();
  }
  if (!email || !code) {
    throw new Error("Reset requires both email and code.");
  }
  const response = await fetch(`${API_BASE_URL}/oauth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code, newPassword: password }),
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(
      toReadableError(payload, "Unable to reset password. Please try again.")
    );
  }
  return payload;
}

export function getAuthApiBaseUrl() {
  return API_BASE_URL;
}
